import React, { Component } from 'react';

import { css } from '@emotion/core';
import styled from '@emotion/styled';
import {
  Sidebar,
  SidebarAccountDropdown,
  SidebarDropdownButton,
  SidebarFooter,
  SidebarFooterItem,
  SidebarHeader,
  SidebarItem,
  SidebarMenu,
  SidebarMenuHeader,
  SidebarMenuItem,
  SidebarNav,
  SidebarShopButton,
} from '@lightspeed/cirrus-sidebar';
import Text from '@lightspeed/cirrus-text';
import defaultTheme from '@lightspeed/cirrus-tokens/dist/theme/default';
import { color, spacing } from '@lightspeed/cirrus-tokens/utils';
import { PillBadge } from '@lightspeed/flame/Badge';
import { Box, Flex } from '@lightspeed/flame/Core';
import { IconCash } from '@lightspeed/flame/Icon/Cash';
import { IconContent } from '@lightspeed/flame/Icon/Content';
import { IconCustomers } from '@lightspeed/flame/Icon/Customers';
import { IconHardware } from '@lightspeed/flame/Icon/Hardware';
import { IconHelp } from '@lightspeed/flame/Icon/Help';
import { IconHome } from '@lightspeed/flame/Icon/Home';
import { IconInventory } from '@lightspeed/flame/Icon/Inventory';
import { IconLockClosed } from '@lightspeed/flame/Icon/LockClosed';
import { IconRegister } from '@lightspeed/flame/Icon/Register';
import { IconSettings } from '@lightspeed/flame/Icon/Settings';
import { IconShop } from '@lightspeed/flame/Icon/Shop';
import { IconStatistics } from '@lightspeed/flame/Icon/Statistics';
import { IconSubscription } from '@lightspeed/flame/Icon/Subscription';
import { IconSuppliers } from '@lightspeed/flame/Icon/Suppliers';
import { IconTools } from '@lightspeed/flame/Icon/Tools';
import { ThemeProvider } from 'emotion-theming';
import get from 'lodash/get';

import { createReactRenderer } from 'helpers/react-renderer';
import { decodeHtml } from 'helpers/utils';
import { NotificationBadge } from 'pages/components/Sidebar/NotificationBadge.style';
import history from 'pages/router/history';
import store from 'pages/store';
import { toBoolean } from 'pages/utils/sanitize';
import IconAccounting from 'pages/views/accounting/IconAccounting';
import StatusIndicator from 'pages/views/devices/components/StatusIndicator';
import { socketClient } from 'pages/views/devices/socketClient';
import { CFD_MESSAGES } from 'pages/views/devices/socketClient/types';
import { safeGetItem, safeSetItem } from 'pages/views/devices/utils/storage';
import { hasFeature } from 'root/api/admin/features';
import { NetworkHealthTool } from 'root/apps/NetworkHealthTool/NetworkHealthTool';
import { SidebarMASNotificationBadge } from 'root/apps/pages/views/settings/MAS/SidebarMASNotificationBadge';
import {
  checkNavigationBlockers,
  checkParcelNavigationBlockers,
  isPathWithParcelNavigationBlocker,
  isUnifiedBlockerEnabled,
} from 'root/apps/utils/navigationBlocker';

import { LockButton as SidebarLockButton } from './LockButton/LockButton.style';
import { LightspeedLogo } from './Logos/index.tsx';
import { LsidProductSwitcher } from './LsidProductSwitcher';
import SidebarTimeclock from './Timeclock';

const FREE_TIER_MODULE = 'customers.invoicing_integration.free_tier';

const Wrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SidebarExtended = styled(Sidebar)`
  background-color: ${(props) => (props.lsidProductSwitcherEnabled ? '#18191d' : '#232a3b')};
`;

const SidebarFooterItemPin = styled(SidebarFooterItem)`
  cursor: pointer;
  padding-left: ${spacing(4)};
  padding-right: ${spacing(4)};

  &.active {
    .cr-icon {
      --cr-icon-base-1-fill: ${color('orange-200')};
      --cr-icon-base-2-fill: ${color('orange-200')};
    }
  }
`;

// allow SidebarItem to grow to the full width of the Sidebar
const SidebarItemStyled = styled(SidebarItem)`
  flex: 1;
  div {
    flex: 1;
  }
`;

const PillBadgeStyled = styled(PillBadge)`
  font-size: 0.5em;
  height: 20px;
  margin-right: 10px;
  margin-top: 2px;
  text-transform: uppercase;
`;

const SidebarHeaderContainer = styled('div')`
  background-color: ${(props) => (props.lsidProductSwitcherEnabled ? '#18191d' : '#232a3b')};
`;

export default class RetailSidebar extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isPinActive: document.body.classList.contains('drawer_always_open'),
      isEmployeeMenuOpen: false,
      registerName: props.registerName,
      activeMenuItem:
        window.location.pathname === '/register.php'
          ? 'register'
          : get(window, 'breadcrumbs[0].name'),
      hasSupportNetworkHealthTool: false,
    };

    this.navigate = this.navigate.bind(this);
    this.openEmployeeMenu = this.openEmployeeMenu.bind(this);
    this.closeEmployeeMenu = this.closeEmployeeMenu.bind(this);
  }

  componentDidMount() {
    this.historyChangeListener = this.listenToHistoryChange();
    this.registerChangeListener = this.listenToRegisterChange();

    // iOS 7 on iPad can't figure on what the window height is
    if (
      navigator.userAgent.match(/iPad;.*CPU.*OS 7_\d/i) &&
      !window.navigator.standalone &&
      !navigator.userAgent.match(/cloudipad/i)
    ) {
      document.getElementsByTagName('html')[0].classList.add('ipad', 'ios7');
    }

    hasFeature('SupportNetworkHealthTool').then((hasSupportNetworkHealthTool) => {
      this.setState((prevState) => ({
        ...prevState,
        hasSupportNetworkHealthTool,
      }));
    });
  }

  componentWillUnmount() {
    this.historyChangeListener?.unsubscribe();
    this.registerChangeListener?.unsubscribe();
  }

  listenToHistoryChange() {
    return window.merchantos.asyncSubscribe.subscribeToFormCall(
      'react.mount.topbar',
      ({ props }) => {
        this.setState({
          activeMenuItem: get(props, 'crumbs.[0].name'),
        });
      }
    );
  }

  listenToRegisterChange() {
    return window.merchantos.asyncSubscribe.subscribeToFormCall(
      'register.register_switched',
      ({ registerName }) => {
        this.setState({
          registerName,
        });
      }
    );
  }

  openEmployeeMenu() {
    this.setState(() => ({
      isEmployeeMenuOpen: true,
    }));
  }

  closeEmployeeMenu() {
    setTimeout(() => {
      this.setState(() => ({
        isEmployeeMenuOpen: false,
      }));
    });
  }

  togglePin(event) {
    event.preventDefault();
    event.stopPropagation();
    const { isPinActive } = this.state;

    this.setState({
      isPinActive: !isPinActive,
    });

    window.merchantos.sidebar.toggleDrawerAlwaysOpen();
  }

  navigate({ e, preventNewTab, action = () => {}, isFinancialServicesTab = false }) {
    e.preventDefault();

    if (window.lsCloud.Modules.hasModule('navigation.reroute_to_subscriptions_page_when_locked')) {
      const { isExpired } = window.merchantos.account;
      if (isExpired && !isFinancialServicesTab) {
        // if they click on any other sidebar menu item other than financial services, they'll be redirected to the subscriptions page
        window.merchantos.tabs.show('subscription');
        return;
      }
    }

    // Open in new tab with: mouse middle click, cmd or ctrl + click
    if (!preventNewTab && (e.button === 1 || e.metaKey || e.ctrlKey)) return;

    window.clearParents();
    window.refreshContextRequests('view', 0);

    window.saveOnChanges().done(() => {
      if (!window.merchantos.register.confirmForUnsavedCreditPayments()) {
        return;
      }

      const netPromoter = document.getElementById('netpromoter');
      if (netPromoter && netPromoter.offsetParent) {
        window.hideNetPromoter();
      }

      const runNavigation = () => {
        action();
        document.body.classList.remove('navigable');
        window.merchantos.register.partial_payments.forceCancel();
        socketClient.send(CFD_MESSAGES.HIDE_SALE);
      };

      if (isUnifiedBlockerEnabled()) {
        // Mark that we've already prompted for unsaved changes, so the nested PageRouter.goTo
        // triggered by runNavigation skips its own check and avoids double-prompting.
        window.navigationBlockersSkipNextCheck = true;
        checkNavigationBlockers()
          .then(runNavigation)
          .catch(() => {
            // Save failed; the registered blocker surfaces its own error UI. Navigation stays on current page.
          })
          .finally(() => {
            window.navigationBlockersSkipNextCheck = false;
          });
      } else {
        // LEGACY (FF off): parcel-only path. Remove when ui.navigation_blocker.unified is fully on.
        const hasFreeTierModule = window.lsCloud.Modules.hasModule(FREE_TIER_MODULE);
        const currentReactPath = history.location && history.location.pathname;
        const shouldCheckParcelBlockers =
          hasFreeTierModule &&
          currentReactPath &&
          isPathWithParcelNavigationBlocker(currentReactPath);

        if (shouldCheckParcelBlockers) {
          window.parcelNavigationBlockersSkipNextCheck = true;
          checkParcelNavigationBlockers()
            .then(runNavigation)
            .catch(() => {
              // Save failed; MFE is notified via onSaveError and shows its own toast. Navigation stays on current page.
            })
            .finally(() => {
              window.parcelNavigationBlockersSkipNextCheck = false;
            });
        } else {
          runNavigation();
        }
      }
    });
  }

  render() {
    const {
      accountId,
      isMultiShop,
      shopName,
      employeeName,
      modules,
      apps,
      sidebarMenu,
      ecomLink,
      logoutLink,
      lsidDomain,
      lsidAppDomain,
      lsidOidcAppName,
      nuorderGrant,
    } = this.props;
    const { activeMenuItem, isPinActive, registerName } = this.state;
    const ecomLiteLink = '/admin/lightspeed-online/sso';
    const paymentsPath = '/payments/lightspeed-payments';
    const items = [
      ~sidebarMenu.indexOf('home')
        ? {
            title: 'Home',
            icon: <IconHome />,
            children: 'Home',
            href: '/home',
            active: activeMenuItem === 'home',
            onClick: (e) => {
              this.navigate({ e, action: () => window.PageRouter.goTo('/home') });
            },
            'data-test': 'sidebar-home-link',
            'data-localize': 'Sidebar-Home',
          }
        : undefined,
      ~sidebarMenu.indexOf('sale')
        ? {
            title: 'Sales',
            icon: <IconRegister />,
            children: 'Sales',
            href: '/?form_name=ui_tab&tab=register',
            active: activeMenuItem === 'register',
            onClick: (e) => {
              this.navigate({
                e,
                action: () => window.merchantos.tabs.show('register'),
              });
            },
            'data-test': 'sidebar-sales-link',
            'data-localize': 'Sidebar-Sales',
          }
        : undefined,
      ~sidebarMenu.indexOf('subscriptions')
        ? {
            title: 'Subscriptions',
            icon: <IconSubscription />,
            children: 'Subscriptions',
            href: '/subscriptions',
            active: activeMenuItem === 'subscriptions',
            onClick: (e) => {
              this.navigate({ e, action: () => window.PageRouter.goTo('/subscriptions') });
            },
            'data-test': 'sidebar-subscriptions-link',
            'data-localize': 'Sidebar-Subscriptions',
          }
        : undefined,
      ~sidebarMenu.indexOf('inventory')
        ? {
            title: 'Inventory',
            icon: <IconInventory />,
            children: 'Inventory',
            href: '/?form_name=ui_tab&tab=inventory',
            active: activeMenuItem === 'inventory',
            onClick: (e) => {
              this.navigate({
                e,
                action: () => {
                  window.merchantos.tabs.show('inventory');

                  // Wait for the inventory section to render before triggering modal
                  const observer = new MutationObserver((mutations, obs) => {
                    const inventorySection = document.querySelector(
                      '[data-react-root="triggerPoModeModalOnLoad"]'
                    );
                    if (inventorySection && window.asyncHooks?.newNuorderVendorIntegrationHook) {
                      obs.disconnect();
                      window.asyncHooks.newNuorderVendorIntegrationHook();
                    }
                  });

                  observer.observe(document.body, {
                    childList: true,
                    subtree: true,
                  });
                },
              });
            },
            'data-test': 'sidebar-inventory-link',
            'data-localize': 'Sidebar-Inventory',
          }
        : undefined,
      ~sidebarMenu.indexOf('marketplace')
        ? {
            title: 'Supplier Market',
            icon: <IconSuppliers />,
            children: 'Supplier Market',
            href: '/inventory/suppliers',
            active: activeMenuItem === 'marketplace',
            onClick: (e) => {
              this.navigate({
                e,
                action: () => window.PageRouter.goTo('/inventory/suppliers'),
              });
            },
            'data-test': 'sidebar-marketplace-link',
            'data-localize': 'Sidebar-Marketplace-Cards',
          }
        : undefined,
      ~sidebarMenu.indexOf('service')
        ? {
            title: 'Service',
            icon: <IconTools />,
            children: 'Service',
            href: '/?form_name=ui_tab&tab=service',
            active: activeMenuItem === 'service',
            onClick: (e) => {
              this.navigate({ e, action: () => window.merchantos.tabs.show('service') });
            },
            'data-test': 'sidebar-service-link',
            'data-localize': 'Sidebar-Service',
          }
        : undefined,
      ~sidebarMenu.indexOf('customers')
        ? {
            title: 'Customers',
            icon: <IconCustomers />,
            children: 'Customers',
            href: '/?form_name=ui_tab&tab=customers',
            active: activeMenuItem === 'customers',
            onClick: (e) => {
              this.navigate({ e, action: () => window.merchantos.tabs.show('customers') });
            },
            'data-test': 'sidebar-customers-link',
            'data-localize': 'Sidebar-Customers',
          }
        : undefined,
      ~sidebarMenu.indexOf('wholesale')
        ? {
            title: 'Wholesale',
            icon: (
              <img height={16} width={16} alt="Wholesale" src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/wholesale/carriage.svg" />
            ),
            children: (
              <Flex justifyContent="space-between" alignItems="center">
                <span>Wholesale</span>
                <PillBadgeStyled>New</PillBadgeStyled>
              </Flex>
            ),
            href: '/wholesale',
            active: activeMenuItem === 'wholesale',
            onClick: (e) => {
              this.navigate({ e, action: () => window.PageRouter.goTo('/wholesale') });
            },
            'data-test': 'sidebar-wholesale-link',
            'data-localize': 'Sidebar-Wholesale',
          }
        : undefined,
      ~sidebarMenu.indexOf('reports')
        ? {
            title: 'Reports',
            icon: <IconStatistics />,
            children: 'Reports',
            href: '/?form_name=ui_tab&tab=reports',
            active: activeMenuItem === 'reports',
            onClick: (e) => {
              this.navigate({ e, action: () => window.merchantos.tabs.show('reports') });
            },
            'data-test': 'sidebar-reports-link',
            'data-localize': 'Sidebar-Reports',
          }
        : undefined,
      ~sidebarMenu.indexOf('accounting')
        ? {
            title: 'Accounting',
            icon: <IconAccounting />,
            children: 'Accounting',
            href: '/accounting',
            active: activeMenuItem === 'accounting',
            onClick: (e) => {
              this.navigate({ e, action: () => window.PageRouter.goTo('/accounting') });
            },
            'data-test': 'sidebar-accounting-link',
            'data-localize': 'Sidebar-Accounting',
          }
        : undefined,
      ~sidebarMenu.indexOf('account-overview')
        ? {
            title: 'Account Overview',
            icon: <IconShop />,
            children: 'Account Overview',
            active: activeMenuItem === 'account-overview',
            href: '/account-overview',
            onClick: (e) => {
              this.navigate({ e, action: () => window.PageRouter.goTo('/account-overview') });
            },
            'data-test': 'sidebar-account-overview-link',
            'data-localize': 'Sidebar-Account-Overview',
          }
        : undefined,
      ~sidebarMenu.indexOf('financial_services')
        ? {
            title: 'Financial Services',
            icon: <IconCash />,
            children: 'Financial Services',
            active: activeMenuItem === 'financial_services',
            href: '/?form_name=ui_tab&tab=financial_services',
            onClick: (e) => {
              this.navigate({
                e,
                action: () => window.PageRouter.goTo('/financial-services/lightspeed-payments'),
                isFinancialServicesTab: true,
              });
            },
            'data-test': 'sidebar-financial_services-link',
            'data-localize': 'Sidebar-Financial-Services',
          }
        : undefined,
      ~sidebarMenu.indexOf('settings')
        ? {
            title: 'Settings',
            icon: <IconSettings />,
            children: (
              <Flex justifyContent="space-between" alignItems="center" flex="1">
                <span>Settings</span>
                <SidebarMASNotificationBadge />
              </Flex>
            ),
            active: activeMenuItem === 'admin',
            href: '/?form_name=ui_tab&tab=admin',
            onClick: (e) => {
              this.navigate({ e, action: () => window.merchantos.tabs.show('admin') });
            },
            'data-test': 'sidebar-settings-link',
            'data-localize': 'Sidebar-Settings',
          }
        : undefined,
      ~sidebarMenu.indexOf('system')
        ? {
            title: 'System',
            icon: <IconContent />,
            children: 'System',
            href: '/?form_name=ui_tab&tab=system',
            active: activeMenuItem === 'system',
            onClick: (e) => {
              this.navigate({ e, action: () => window.merchantos.tabs.show('system') });
            },
            'data-test': 'sidebar-system-link',
            'data-localize': 'Sidebar-System',
          }
        : undefined,
    ].filter((e) => e);

    return (
      <ThemeProvider theme={defaultTheme}>
        <SidebarExtended lsidProductSwitcherEnabled>
          <SidebarHeader>
            <SidebarHeaderContainer lsidProductSwitcherEnabled>
              <Box px={2}>
                <Flex height="54px" alignItems="center" justifyContent="start">
                  <LightspeedLogo />
                </Flex>
                <LsidProductSwitcher
                  domain={lsidDomain}
                  appDomain={lsidAppDomain}
                  showEcom={!!~apps.indexOf('ecom')}
                  showEcomLite={!!~modules.indexOf('integration.ecom_lite')}
                  cSeriesHideDropdown={!!~modules.indexOf('ecom.cseries.hide_dropdown')}
                  ecomLink={ecomLink}
                  ecomLiteLink={ecomLiteLink}
                  navigate={this.navigate}
                  appName={lsidOidcAppName}
                />
              </Box>
            </SidebarHeaderContainer>

            <SidebarShopButton
              title={`${isMultiShop ? 'Shop and ' : ''}Register`}
              active={activeMenuItem === 'choose_register' || activeMenuItem === 'choose_shop'}
              onClick={(e) => {
                this.navigate({
                  e,
                  action: () =>
                    window.merchantos.tabs.show(isMultiShop ? 'choose_shop' : 'choose_register'),
                });
              }}
              data-test="sidebar-choose-register-link"
            >
              <Text
                element="div"
                size="small"
                css={css`
                  line-height: 1.4;
                `}
                className="cr-truncate notranslate"
              >
                {decodeHtml(shopName)}
              </Text>
              <Text
                element="div"
                size="small"
                bold
                css={css`
                  line-height: 1.4;
                `}
                className={`cr-truncate ${registerName ? 'notranslate' : ''}`}
              >
                {registerName ? decodeHtml(registerName) : 'Choose register'}
              </Text>
            </SidebarShopButton>

            <Wrapper>
              <SidebarAccountDropdown
                isOpen={this.state.isEmployeeMenuOpen}
                onOpen={this.openEmployeeMenu}
                onClose={this.closeEmployeeMenu}
                target={({ targetProps, targetEvents, isOpen }) => (
                  <SidebarDropdownButton
                    title="My Account"
                    {...targetProps}
                    {...targetEvents}
                    active={isOpen}
                    className="notranslate"
                    data-test="sidebar-account-dropdown"
                    css={css`
                      margin-right: ${~modules.indexOf('timeclock') ? '6px' : 'inherit'};
                      max-width: ${~modules.indexOf('timeclock') ? '58%' : 'calc(100% - 24px)'};
                      width: ${!~modules.indexOf('timeclock') ? 'calc(100% - 24px)' : null};
                    `}
                  >
                    {decodeHtml(employeeName)}
                  </SidebarDropdownButton>
                )}
              >
                <SidebarMenuHeader>
                  <span>Account ID</span>
                  <var data-test="sidebar-account-id">{accountId}</var>
                </SidebarMenuHeader>
                <SidebarMenu>
                  {~modules.indexOf('timeclock') ? (
                    <SidebarMenuItem
                      title="Time clock"
                      onClick={(e) => {
                        this.navigate({
                          e,
                          preventNewTab: true,
                          action: () => {
                            window.merchantos.tabs.show('clock_inout');
                            this.closeEmployeeMenu();
                          },
                        });
                      }}
                      href="/?form_name=ui_tab&amp;tab=clock_inout"
                      data-test="sidebar-account-timeclock-link"
                    >
                      Time clock
                    </SidebarMenuItem>
                  ) : null}
                  <SidebarMenuItem
                    title="Change employee"
                    onClick={(e) => {
                      this.navigate({
                        e,
                        preventNewTab: true,
                        action: () => {
                          window.verifyWithPIN();
                          this.closeEmployeeMenu();
                        },
                      });
                    }}
                    css={css`
                      cursor: pointer;
                    `}
                    data-test="sidebar-account-change-employee-link"
                  >
                    Change employee
                  </SidebarMenuItem>
                  <SidebarMenuItem
                    title="Employee settings"
                    onClick={(e) => {
                      this.navigate({
                        e,
                        action: () => {
                          window.merchantos.tabs.show('user');
                          this.closeEmployeeMenu();
                        },
                      });
                    }}
                    href="/?form_name=ui_tab&amp;tab=user"
                  >
                    Employee settings
                  </SidebarMenuItem>
                  <SidebarMenuItem
                    title="Sign out"
                    onClick={(e) => {
                      e.preventDefault();
                      // eslint-disable-next-line no-alert
                      if (confirm('Are you sure you want to sign out?')) {
                        this.closeEmployeeMenu();
                        socketClient.send(CFD_MESSAGES.HIDE_SALE);
                        window.location.href = logoutLink;
                      }
                    }}
                    href={logoutLink}
                    danger
                    data-test="sidebar-account-logout-link"
                  >
                    Sign out
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarAccountDropdown>
              {~modules.indexOf('timeclock') ? (
                <SidebarTimeclock
                  title="Time clock"
                  onClick={(e) => {
                    this.navigate({
                      e,
                      preventNewTab: true,
                      action: () => window.merchantos.tabs.show('clock_inout'),
                    });
                  }}
                  data-test="sidebar-timeclock-link"
                />
              ) : null}
            </Wrapper>
          </SidebarHeader>

          <SidebarNav>
            <ul data-test="sidebar-links">
              {items.map((item) => (
                <SidebarItemStyled
                  key={item.title}
                  id={`${item.title.toLowerCase().split(' ').join('-')}-sidebar-link`}
                  {...item}
                />
              ))}
            </ul>
          </SidebarNav>

          {sidebarMenu.includes('devices') && (
            <SidebarItem
              title="Devices"
              icon={<IconHardware />}
              // eslint-disable-next-line react/no-children-prop
              children="Devices"
              href="/devices"
              active={activeMenuItem === 'devices'}
              onClick={(e) => {
                this.navigate({ e, action: () => window.PageRouter.goTo('/devices') });
              }}
              notifications={<StatusIndicator />}
              data-test="sidebar-devices-link"
              data-localize="Sidebar-Devices"
            />
          )}

          <SidebarLockButton
            title="Lock"
            icon={<IconLockClosed />}
            // eslint-disable-next-line react/no-children-prop
            children="Lock"
            onClick={(e) => {
              e.preventDefault();
              if (document.body.classList.contains('employee-no-pin')) {
                // eslint-disable-next-line no-alert
                if (window.confirm('You do not have a PIN set. Would you like to set a PIN now?')) {
                  window.location = '/change_pin.php';
                  return;
                }
              }
              window.verifyWithPIN();
              socketClient.send(CFD_MESSAGES.HIDE_SALE);
            }}
            centered
            data-test="sidebar-lock-link"
          />

          {this.state.hasSupportNetworkHealthTool &&
            toBoolean(window.merchantos.session.getShop()?.networkHealthTool) && (
              <NetworkHealthTool />
            )}

          <SidebarFooter>
            <SidebarFooterItem
              title="Help"
              icon={<IconHelp />}
              className="js-sidebar-help cr-flex-grow"
              css={css`
                cursor: pointer;
              `}
              data-test="sidebar-help-link"
              data-localize="Sidebar-Help"
            >
              Help
            </SidebarFooterItem>

            <SidebarFooterItemPin
              title="Keep drawer open regardless of window size"
              className={`${isPinActive ? 'active' : ''}`}
              onClick={(event) => this.togglePin(event)}
            >
              <svg className="cr-icon cr-icon-pin">
                <use xlinkHref="#cr-icon-pin" />
              </svg>
            </SidebarFooterItemPin>
          </SidebarFooter>
        </SidebarExtended>
      </ThemeProvider>
    );
  }
}

RetailSidebar.defaultProps = {
  modules: [],
  apps: [],
  sidebarMenu: [],
};

const RenderedComponent = createReactRenderer({
  component: RetailSidebar,
  store,
  selector: '[data-react-root="Sidebar"]',
});

RenderedComponent.mount();
